import { useState } from 'react';
import { User, Moon, Sun, Monitor, Bell, Shield } from 'lucide-react';
import { useThemeStore } from '../stores/themeStore';
import { useAuthStore } from '../stores/authStore';
import { cn } from '../utils/api';

export default function SettingsPage() {
  const { theme, setTheme } = useThemeStore();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'general' | 'account' | 'notifications'>('general');

  const tabs = [
    { id: 'general', label: 'General', icon: User },
    { id: 'account', label: 'Account', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
  ];

  return (
    <div className="h-full flex">
      {/* Sidebar */}
      <div className="w-64 border-r border-legion-border bg-legion-card/50 p-4">
        <h1 className="text-xl font-semibold mb-6">Settings</h1>
        <nav className="space-y-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-left",
                activeTab === tab.id
                  ? "bg-indigo-600/20 text-indigo-400"
                  : "hover:bg-white/5 text-gray-300"
              )}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-2xl">
          {activeTab === 'general' && (
            <div className="space-y-8">
              <section>
                <h2 className="text-lg font-semibold mb-4">Appearance</h2>
                <div className="bg-legion-card border border-legion-border rounded-xl p-6">
                  <label className="block text-sm font-medium mb-3">Theme</label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setTheme('light')}
                      className={cn(
                        "flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors",
                        theme === 'light'
                          ? "border-indigo-500 bg-indigo-500/10"
                          : "border-legion-border hover:border-gray-500"
                      )}
                    >
                      <Sun className="w-4 h-4" />
                      Light
                    </button>
                    <button
                      onClick={() => setTheme('dark')}
                      className={cn(
                        "flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors",
                        theme === 'dark'
                          ? "border-indigo-500 bg-indigo-500/10"
                          : "border-legion-border hover:border-gray-500"
                      )}
                    >
                      <Moon className="w-4 h-4" />
                      Dark
                    </button>
                    <button
                      onClick={() => setTheme('system')}
                      className={cn(
                        "flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors",
                        theme === 'system'
                          ? "border-indigo-500 bg-indigo-500/10"
                          : "border-legion-border hover:border-gray-500"
                      )}
                    >
                      <Monitor className="w-4 h-4" />
                      System
                    </button>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-lg font-semibold mb-4">Language & Region</h2>
                <div className="bg-legion-card border border-legion-border rounded-xl p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Language</label>
                    <select className="w-full px-3 py-2 bg-legion-dark border border-legion-border rounded-lg focus:outline-none focus:border-indigo-500">
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                    </select>
                  </div>
                </div>
              </section>
            </div>
          )}

          {activeTab === 'account' && (
            <div className="space-y-8">
              <section>
                <h2 className="text-lg font-semibold mb-4">Profile</h2>
                <div className="bg-legion-card border border-legion-border rounded-xl p-6 space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-2xl font-bold">
                      {user?.displayName?.[0] || user?.username?.[0] || 'U'}
                    </div>
                    <div>
                      <p className="font-medium">{user?.displayName || user?.username}</p>
                      <p className="text-sm text-gray-500">{user?.email}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Display Name</label>
                      <input
                        type="text"
                        defaultValue={user?.displayName}
                        className="w-full px-3 py-2 bg-legion-dark border border-legion-border rounded-lg focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Username</label>
                      <input
                        type="text"
                        defaultValue={user?.username}
                        disabled
                        className="w-full px-3 py-2 bg-legion-dark border border-legion-border rounded-lg text-gray-500"
                      />
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-lg font-semibold mb-4">Security</h2>
                <div className="bg-legion-card border border-legion-border rounded-xl p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Change Password</p>
                      <p className="text-sm text-gray-500">Update your account password</p>
                    </div>
                    <button className="px-4 py-2 border border-legion-border rounded-lg hover:bg-white/5 transition-colors">
                      Change
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Two-Factor Authentication</p>
                      <p className="text-sm text-gray-500">Add an extra layer of security</p>
                    </div>
                    <button className="px-4 py-2 border border-legion-border rounded-lg hover:bg-white/5 transition-colors">
                      Enable
                    </button>
                  </div>
                </div>
              </section>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="space-y-8">
              <section>
                <h2 className="text-lg font-semibold mb-4">Notification Preferences</h2>
                <div className="bg-legion-card border border-legion-border rounded-xl p-6 space-y-4">
                  {[
                    { label: 'Email notifications', desc: 'Receive updates via email' },
                    { label: 'Research completed', desc: 'Get notified when research finishes' },
                    { label: 'Operator tasks', desc: 'Notifications for autonomous tasks' },
                    { label: 'New features', desc: 'Learn about new LEGION AI features' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{item.label}</p>
                        <p className="text-sm text-gray-500">{item.desc}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" defaultChecked={i < 2} />
                        <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
